/**
 * Framework-agnostic session WebSocket client (T4). Owns the connection lifecycle, version-gated view
 * application, action promises with idempotent retry, and ephemeral event fan-out. No React here; the
 * React binding lives in `useSessionClient.ts`. See docs/ARCHITECTURE.md §5 and §8.
 */
import {
  ClientPresenterView,
  PROTOCOL_VERSION,
  ServerMessage,
  type ActionOutcome,
  type ActionTarget,
  type ClientView,
  type SelfInfo,
  type ServerEventMessage,
} from '@momentum/shared';
import type { z } from 'zod';

/** The `presenter_view` frame's `viewer` field (T7): a simulated participant's eyes, or the spectator view. */
export type PresenterViewer = z.infer<typeof ClientPresenterView>['viewer'];

/** Minimal WebSocket surface the client depends on, so tests can inject a fake implementation. */
export interface WebSocketLike {
  readyState: number;
  onopen: ((this: WebSocketLike, ev: unknown) => void) | null;
  onclose: ((this: WebSocketLike, ev: { code: number; reason: string }) => void) | null;
  onerror: ((this: WebSocketLike, ev: unknown) => void) | null;
  onmessage: ((this: WebSocketLike, ev: { data: string }) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}
export type WebSocketConstructor = new (url: string) => WebSocketLike;

const WS_OPEN = 1;

export type ClientStatus = 'connecting' | 'connected' | 'reconnecting' | 'closed';

export type ClosedReason = Extract<ServerMessage, { type: 'closed' }>['reason'];
export interface ClosedInfo {
  reason: ClosedReason;
  message: string;
}

export interface SessionClientSnapshot {
  view: ClientView | null;
  self: SelfInfo | null;
  status: ClientStatus;
  serverTimeOffset: number;
  closed: ClosedInfo | null;
}

export interface SessionClientOptions {
  sessionId: string;
  token: string;
  wsUrl: string;
  /** Defaults to 'player'. Use 'display' for spectator screens, 'presenter' for presenter connections. */
  clientKind?: 'player' | 'display' | 'presenter';
  /** Injectable for tests; defaults to the global WebSocket constructor. */
  WebSocketImpl?: WebSocketConstructor;
  /** Overridable for tests. Defaults to real timers. */
  now?: () => number;
}

export interface SendOptions {
  expectedVersion?: number;
  asParticipantId?: string;
}

export interface SessionClient {
  /** useSyncExternalStore-compatible: same reference until state actually changes. */
  getSnapshot(): SessionClientSnapshot;
  subscribe(listener: () => void): () => void;
  /** Alias of subscribe, named per the view/status change contract. */
  onChange(listener: () => void): () => void;
  onEvent(listener: (event: ServerEventMessage) => void): () => void;
  send(name: string, payload: unknown, target: ActionTarget, opts?: SendOptions): Promise<ActionOutcome>;
  /**
   * Presenter connections only (T7): switches the projected perspective (`ClientView.viewingAs`) to a
   * simulated participant's eyes, or back to the spectator view. Fire-and-forget like `resync` — the server
   * answers with a `state` frame carrying the new projection rather than an ack/reject, since this changes
   * only what the presenter's own connection sees (docs/ARCHITECTURE.md §4, "Projection viewer").
   */
  setPresenterView(viewer: PresenterViewer): void;
  close(): void;
}

const ACTION_TIMEOUT_MS = 10_000;
const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 8_000;
const PING_INTERVAL_MS = 10_000;
const MAX_SEND_ATTEMPTS = 2;

interface PendingAction {
  actionId: string;
  target: ActionTarget;
  name: string;
  payload: unknown;
  expectedVersion?: number;
  asParticipantId?: string;
  attempts: number;
  resolve: (outcome: ActionOutcome) => void;
  timeoutHandle: ReturnType<typeof setTimeout>;
}

function defaultWebSocketImpl(): WebSocketConstructor {
  const ctor = (globalThis as { WebSocket?: unknown }).WebSocket;
  if (!ctor) {
    throw new Error('No WebSocket implementation available; pass WebSocketImpl explicitly');
  }
  return ctor as WebSocketConstructor;
}

export function createSessionClient(options: SessionClientOptions): SessionClient {
  const { sessionId, token, wsUrl } = options;
  const clientKind = options.clientKind ?? 'player';
  const WSImpl = options.WebSocketImpl ?? defaultWebSocketImpl();
  const now = options.now ?? (() => Date.now());

  let ws: WebSocketLike | null = null;
  let status: ClientStatus = 'connecting';
  let view: ClientView | null = null;
  let self: SelfInfo | null = null;
  let serverTimeOffset = 0;
  let closedInfo: ClosedInfo | null = null;
  let stopped = false;
  let everConnected = false;
  let awaitingResync = false;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;

  const pending = new Map<string, PendingAction>();
  const changeListeners = new Set<() => void>();
  const eventListeners = new Set<(event: ServerEventMessage) => void>();

  let snapshot: SessionClientSnapshot = { view, self, status, serverTimeOffset, closed: closedInfo };

  function emitChange(): void {
    snapshot = { view, self, status, serverTimeOffset, closed: closedInfo };
    for (const listener of changeListeners) listener();
  }

  function sendRaw(message: Record<string, unknown>): void {
    if (!ws || ws.readyState !== WS_OPEN) return;
    ws.send(JSON.stringify(message));
  }

  function tryTransmit(entry: PendingAction): void {
    if (!ws || ws.readyState !== WS_OPEN) return;
    if (entry.attempts >= MAX_SEND_ATTEMPTS) return;
    sendRaw({
      type: 'action',
      actionId: entry.actionId,
      target: entry.target,
      name: entry.name,
      payload: entry.payload,
      expectedVersion: entry.expectedVersion,
      asParticipantId: entry.asParticipantId,
    });
    entry.attempts += 1;
  }

  function stopPing(): void {
    if (pingTimer !== null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  }

  function startPing(): void {
    stopPing();
    pingTimer = setInterval(() => {
      sendRaw({ type: 'ping', t: now() });
    }, PING_INTERVAL_MS);
  }

  function clearReconnectTimer(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function scheduleReconnect(): void {
    if (stopped) return;
    status = 'reconnecting';
    emitChange();
    const base = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** reconnectAttempt);
    const jittered = base / 2 + Math.random() * (base / 2);
    reconnectAttempt += 1;
    clearReconnectTimer();
    reconnectTimer = setTimeout(() => connect(), Math.min(RECONNECT_MAX_MS, jittered));
  }

  function handleMessage(raw: string): void {
    let json: unknown;
    try {
      json = JSON.parse(raw);
    } catch {
      console.warn('[sessionClient] dropping frame: invalid JSON');
      return;
    }
    const parsed = ServerMessage.safeParse(json);
    if (!parsed.success) {
      console.warn('[sessionClient] dropping frame: failed schema validation', parsed.error.message);
      return;
    }
    const msg = parsed.data;
    switch (msg.type) {
      case 'welcome': {
        view = msg.view;
        self = msg.self;
        serverTimeOffset = msg.serverTime - now();
        status = 'connected';
        closedInfo = null;
        reconnectAttempt = 0;
        const isReconnect = everConnected;
        everConnected = true;
        startPing();
        if (isReconnect) {
          awaitingResync = true;
          sendRaw({ type: 'resync' });
        }
        for (const entry of pending.values()) tryTransmit(entry);
        emitChange();
        break;
      }
      case 'state': {
        if (awaitingResync || view === null || msg.view.version > view.version) {
          view = msg.view;
          awaitingResync = false;
          emitChange();
        }
        break;
      }
      case 'event': {
        for (const listener of eventListeners) listener(msg);
        break;
      }
      case 'ack': {
        const entry = pending.get(msg.actionId);
        if (entry) {
          pending.delete(msg.actionId);
          clearTimeout(entry.timeoutHandle);
          entry.resolve({ ok: true, version: msg.version });
        }
        break;
      }
      case 'reject': {
        const entry = pending.get(msg.actionId);
        if (entry) {
          pending.delete(msg.actionId);
          clearTimeout(entry.timeoutHandle);
          entry.resolve({ ok: false, code: msg.code, message: msg.message, retryable: msg.retryable });
        }
        break;
      }
      case 'error': {
        console.warn('[sessionClient] server error', msg.code, msg.message);
        break;
      }
      case 'pong': {
        serverTimeOffset = msg.serverTime - now();
        break;
      }
      case 'closed': {
        closedInfo = { reason: msg.reason, message: msg.message };
        if (msg.reason === 'server_shutdown') {
          // The server announced a restart; keep the identity and reconnect with backoff (the socket's onclose schedules it).
          status = 'reconnecting';
          stopPing();
          emitChange();
          break;
        }
        status = 'closed';
        stopped = true;
        stopPing();
        clearReconnectTimer();
        emitChange();
        break;
      }
    }
  }

  function connect(): void {
    if (stopped) return;
    const socket = new WSImpl(wsUrl);
    ws = socket;
    socket.onopen = () => {
      sendRaw({ type: 'hello', protocol: PROTOCOL_VERSION, sessionId, token, client: { kind: clientKind } });
    };
    socket.onmessage = (ev) => handleMessage(ev.data);
    socket.onclose = () => {
      stopPing();
      ws = null;
      if (!stopped) scheduleReconnect();
    };
    socket.onerror = () => {
      // onclose follows for real sockets; nothing else to do here.
    };
  }

  connect();

  return {
    getSnapshot() {
      return snapshot;
    },
    subscribe(listener) {
      changeListeners.add(listener);
      return () => changeListeners.delete(listener);
    },
    onChange(listener) {
      changeListeners.add(listener);
      return () => changeListeners.delete(listener);
    },
    onEvent(listener) {
      eventListeners.add(listener);
      return () => eventListeners.delete(listener);
    },
    send(name, payload, target, opts) {
      const actionId = crypto.randomUUID();
      return new Promise<ActionOutcome>((resolve) => {
        const entry: PendingAction = {
          actionId,
          target,
          name,
          payload,
          expectedVersion: opts?.expectedVersion,
          asParticipantId: opts?.asParticipantId,
          attempts: 0,
          resolve,
          timeoutHandle: setTimeout(() => {
            pending.delete(actionId);
            resolve({ ok: false, code: 'internal', message: 'Timed out waiting for a response', retryable: true });
          }, ACTION_TIMEOUT_MS),
        };
        pending.set(actionId, entry);
        tryTransmit(entry);
      });
    },
    setPresenterView(viewer) {
      // The reply is a full snapshot at the SAME session version (a perspective switch is not a transition),
      // so accept the next state frame unconditionally, exactly like a resync.
      awaitingResync = true;
      sendRaw({ type: 'presenter_view', viewer });
    },
    close() {
      stopped = true;
      clearReconnectTimer();
      stopPing();
      for (const entry of pending.values()) {
        clearTimeout(entry.timeoutHandle);
      }
      pending.clear();
      if (ws) {
        try {
          ws.close();
        } catch {
          // Already closing/closed.
        }
      }
      status = 'closed';
      emitChange();
    },
  };
}
